import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { BadgeCheck, X, Send } from 'lucide-react';

/**
 * Claim / update a resource listing. Anyone from the organization can submit
 * corrections (phone, hours, website, closures); admin reviews before publishing.
 */
const ClaimResourceModal = ({ resource, triggerClassName = 'bg-primary text-white' }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const blankFor = (r) => ({
    contact_name: '', contact_email: '', contact_phone: '', role_title: '', organization: '',
    phone: r?.phone || '', website: r?.website || '', address: r?.address || '',
    hours: r?.hours || '', description: r?.description || '', eligibility: r?.eligibility || '',
    temporarily_closed: false, message: '',
  });
  const [form, setForm] = useState(() => blankFor(resource));

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  useEffect(() => { if (open) setForm(blankFor(resource)); }, [open, resource]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error('Please add your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/resources/${resource.resource_id}/claim`, form);
      toast.success(data.message || 'Submitted for review — thank you!');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="claim-resource-btn" className={triggerClassName}>
        <BadgeCheck className="w-4 h-4 mr-1" /> Claim / Update This Listing
      </Button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="claim-resource-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h3 className="font-serif text-xl font-bold text-primary">Claim or Update: {resource?.name}</h3>
                <p className="text-sm text-gray-500">Work here or know this organization? Submit corrections — our team reviews every update before it's published.</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="claim-resource-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Your name *</Label><Input value={form.contact_name} onChange={set('contact_name')} required data-testid="claim-name" /></div>
                <div><Label>Your email *</Label><Input type="email" value={form.contact_email} onChange={set('contact_email')} required data-testid="claim-email" /></div>
                <div><Label>Your role / title</Label><Input value={form.role_title} onChange={set('role_title')} data-testid="claim-role" placeholder="e.g. Program Director" /></div>
                <div><Label>Organization</Label><Input value={form.organization} onChange={set('organization')} data-testid="claim-org" /></div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Proposed updates <span className="font-normal text-gray-400">(leave anything you're not changing)</span></p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Phone</Label><Input value={form.phone} onChange={set('phone')} data-testid="claim-phone" /></div>
                  <div><Label>Website</Label><Input value={form.website} onChange={set('website')} data-testid="claim-website" /></div>
                  <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={set('address')} data-testid="claim-address" /></div>
                  <div className="sm:col-span-2"><Label>Hours</Label><Input value={form.hours} onChange={set('hours')} data-testid="claim-hours" placeholder="e.g. Mon-Fri 9am-5pm" /></div>
                  <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={set('description')} data-testid="claim-description" /></div>
                  <div className="sm:col-span-2"><Label>Eligibility / who it helps</Label><Input value={form.eligibility} onChange={set('eligibility')} data-testid="claim-eligibility" /></div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-3">
                  <input type="checkbox" checked={form.temporarily_closed} onChange={(e) => setForm({ ...form, temporarily_closed: e.target.checked })} data-testid="claim-closed" />
                  This location is temporarily closed
                </label>
              </div>

              <div><Label>Anything else for our team?</Label><Textarea rows={2} value={form.message} onChange={set('message')} data-testid="claim-message" /></div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary-hover text-white" data-testid="claim-submit">
                  <Send className="w-4 h-4 mr-2" /> {submitting ? 'Submitting…' : 'Submit Update for Review'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ClaimResourceModal;
